<script setup lang="ts">
    const academyHomeUrl = useAcademyHomeUrl();
    const selfServiceConsentsUrl = useSelfServiceUrl('consents');

    const { state, isTakingLong, retry } = useDoceboConsentPolling();

    const headlineKey = computed(() =>
        state.value === 'timeout' ? 'academy.consentPending.timeoutHeadline' : 'academy.consentPending.errorHeadline'
    );

    const descriptionKey = computed(() =>
        state.value === 'timeout'
            ? 'academy.consentPending.timeoutDescription'
            : 'academy.consentPending.errorDescription'
    );

    /**
     * Only the visible branch changes when the state does, which a screen reader does not report. The
     * region stays mounted while empty, because one inserted together with its text is not announced.
     */
    const announcementKeys = computed(() => {
        if (state.value === 'timeout' || state.value === 'error') return [headlineKey.value, descriptionKey.value];
        if (isTakingLong.value) return ['academy.consentPending.stillWorking'];

        return [];
    });

    watch(state, (value) => {
        if (value !== 'success') return;

        log.debug(`[AcademyConsentPending] Consent confirmed, redirecting to Academy home: ${academyHomeUrl.value}`);
        /** `replace` keeps the waiting page out of the history. */
        void navigateTo(academyHomeUrl.value, { replace: true });
    });
</script>

<template>
    <div class="error-container">
        <p
            aria-live="polite"
            aria-atomic="true"
            class="sr-only"
        >
            {{ announcementKeys.map((key) => $t(key)).join('. ') }}
        </p>

        <template v-if="state === 'pending' || state === 'success'">
            <LoadingSpinner />
            <div>
                <h1 class="redirect-header">{{ $t('academy.consentPending.headline') }}</h1>
                <p class="error-message">{{ $t('academy.consentPending.description') }}</p>
                <p
                    v-if="isTakingLong"
                    class="error-message"
                >
                    {{ $t('academy.consentPending.stillWorking') }}
                </p>
            </div>
        </template>

        <template v-else>
            <div class="image-wrapper">
                <img
                    src="~/assets/images/404.svg"
                    :alt="$t('general.error')"
                />
            </div>
            <div>
                <h1 class="redirect-header">{{ $t(headlineKey) }}</h1>
                <p class="error-message">{{ $t(descriptionKey) }}</p>
                <div class="consent-actions">
                    <PrimaryButton
                        :button-text="$t('academy.consentPending.retry')"
                        native-button
                        @click="retry"
                    />
                    <PrimaryButton
                        :button-text="$t('academy.consentPending.reviewConsents')"
                        :button-href="selfServiceConsentsUrl"
                        variant="secondary"
                    />
                    <PrimaryButton
                        :button-text="$t('academy.consentPending.backToAcademy')"
                        :button-href="academyHomeUrl"
                        variant="secondary"
                    />
                </div>
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>
    .error-container {
        align-items: center;
        display: flex;
        flex-direction: column;
        justify-content: center;
        margin: 0 auto;
        min-height: 70vh;
        padding: var(--spacing--6) var(--spacing--2);
        text-align: center;
        width: 100%;

        @media (min-width: $br-m) {
            max-width: var(--br-m);
        }

        @media (min-width: $br-l) {
            max-width: var(--br-l);
        }
    }

    .image-wrapper {
        align-items: center;
        display: flex;
        justify-content: center;
        width: 100%;

        img {
            max-width: 12rem;
            width: 100%;
        }
    }

    h1 {
        font-family: 'Figtree', sans-serif;
        font-size: var(--font-xl);
        font-weight: 600;
        line-height: 1.2;

        &.redirect-header {
            font-size: var(--font-m);
            margin-top: var(--spacing);
        }
    }

    .error-message {
        color: var(--gray);
        font-size: var(--font-sm);
        max-width: 58ch;
        padding-top: var(--spacing);
    }

    .consent-actions {
        display: flex;
        flex-direction: column;
        gap: var(--spacing);
        margin: var(--spacing--2) auto 0;
        max-width: 300px;
        width: 100%;
    }
</style>
